<template>
  <div class="questions-list">
    <div class="no-content" v-if="questions.length === 0">
      <span>{{ labels.notFound }}</span>
    </div>
    <div
      v-for="(question, qIdx) in questions"
      :key="`question_${qIdx}`"
      class="question-row"
    >
      <div :class="[showBorder(qIdx)]">
        <b-card
          :header-class="{ 'question-card-header': editable }"
          class="draggable-question"
          :draggable="editable"
          @dragstart="indexPick = qIdx"
          @dragenter="indexDrop = qIdx"
          @dragover="indexOver = qIdx"
          @dragleave="indexOver = undefined"
          @drop="indexOver = undefined"
          @dragend="moveQuestion(indexPick, indexDrop)"
        >
          <header slot="header">
            <b-row>
              <b-col md="12">
                <div class="float-left question-header">
                  <i
                    class="material-icons mr-2 drag-indicator clickable"
                    v-if="editable"
                  >
                    drag_indicator
                  </i>
                  <span>{{ qIdx + 1 }}&deg; {{ labels.question }}</span>
                </div>
                <i
                  v-if="editable"
                  :title="labels.delete"
                  @click="$emit('removeQuestion', qIdx)"
                  class="material-icons clickable float-right"
                >
                  delete_forever
                </i>
              </b-col>
            </b-row>
          </header>
          <b-row>
            <b-col class="pb-3" cols="12" xl="6">
              <div
                v-for="(language, index) in availableLanguages"
                :key="`question_${qIdx}_${index}`"
              >
                <b-form-group
                  v-if="isLanguageSelected(index)"
                  :description="editable ? labels.text.description : ''"
                >
                  <b-form-input
                    v-if="editable"
                    class="name-form-group custom-survey-form-input"
                    :placeholder="labels.text.placeholder"
                    :value="question.text[language]"
                    @input="
                      changeLanguageListProperty(
                        $event,
                        'questions',
                        qIdx,
                        'text',
                        language
                      )
                    "
                  />
                  <p v-else class="readonly-input">
                    {{ question.text[language] }}
                  </p>
                  <label class="custom-input-label">
                    <img
                      :src="
                        getLanguageIcon(availableLanguages[selectedLanguage])
                      "
                      class="language-icon"
                    />
                    {{ labels.text.title }}
                  </label>
                </b-form-group>
              </div>
            </b-col>
            <b-col class="pb-3" cols="12" xl="6">
              <b-form-group
                v-if="editable"
                :label="labels.type.title"
                :description="labels.type.description"
              >
                <v-select
                  :searchable="false"
                  :value="question.type"
                  :options="questionTypes"
                  :closeOnSelect="true"
                  @input="changeListProperty($event, 'questions', qIdx, 'type')"
                >
                  <template slot="option" slot-scope="option">
                    <div class="type-select">
                      <img
                        class="type-icon mr-2"
                        :src="getImageUrl(option.label)"
                      />
                      {{ labels.type[option.label] }}
                    </div>
                  </template>
                  <template slot="no-options">
                    {{ labels.type.notFound }}
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="tag-wrap" :title="option.label">
                      <img :src="getImageUrl(option.label)" />
                      <span class="tag-label">
                        {{ labels.type[option.label] }}
                      </span>
                    </div>
                  </template>
                </v-select>
              </b-form-group>
              <b-form-group v-else>
                <v-select
                  class="readonly-input"
                  :value="question.type"
                  disabled
                >
                  <template slot="selected-option" slot-scope="option">
                    <div class="tag-wrap">
                      <img :src="getImageUrl(option.label)" />
                      <span class="tag-label">
                        {{ labels.type[option.label] }}
                      </span>
                    </div>
                  </template>
                </v-select>
                <label class="custom-input-label">
                  {{ labels.type.title }}
                </label>
              </b-form-group>
            </b-col>
            <b-col cols="12" xl="6">
              <b-form-group
                :label="labels.category.title"
                :description="editable ? labels.category.description : ''"
              >
                <b-form-input
                  v-if="!editable && !question.category"
                  class="readonly-input"
                  :value="labels.category.none"
                  readonly
                />
                <b-form-select
                  v-else-if="editable"
                  class="custom-survey-form-input clickable"
                  :disabled="!editable"
                  :value="question.category"
                  size="lg"
                  required
                  @input="
                    changeListProperty($event, 'questions', qIdx, 'category')
                  "
                >
                  <option
                    v-if="question.category === undefined"
                    :value="undefined"
                  >
                    {{ labels.category.none }}
                  </option>
                  <option :value="null" v-else>
                    {{ labels.category.none }}
                  </option>
                  <option
                    v-for="category in categories"
                    :key="category.category_id"
                    :value="category.category_id"
                  >
                    {{ category.label[availableLanguages[selectedLanguage]] }}
                  </option>
                </b-form-select>
                <p v-else class="readonly-input">
                  {{
                    categories.find(c => c.category_id === question.category)
                      .label[availableLanguages[selectedLanguage]]
                  }}
                </p>
              </b-form-group>
              <b-button
                :id="`popover-add-category-${qIdx}`"
                v-if="editable"
                class="text-secondary float-right"
                variant="link"
                @click="togglePopoverAddCategory(qIdx, true)"
              >
                <div class="question-header">
                  <i class="material-icons">add</i>
                  <small>
                    {{ labels.category.add }}
                  </small>
                </div>
              </b-button>
              <b-popover
                v-if="editable"
                :target="`popover-add-category-${qIdx}`"
                triggers="manual"
                placement="auto"
                :show.sync="question.addCategory"
              >
                <div
                  v-for="(language, index) in availableLanguages"
                  :key="`category_label_${index}`"
                >
                  <b-form-group v-if="isLanguageSelected(index)">
                    <b-form-input
                      class="popover-name-category"
                      v-model="categoryLabel"
                    />
                    <label class="custom-input-label">
                      <img
                        :src="
                          getLanguageIcon(availableLanguages[selectedLanguage])
                        "
                        class="popover-language-icon"
                      />
                      {{ labels.category.name }}
                    </label>
                  </b-form-group>
                </div>
                <b-button
                  size="sm"
                  class="float-left text-secondary"
                  variant="link"
                  @click="togglePopoverAddCategory(qIdx, false)"
                >
                  {{ labels.category.btn.cancel }}
                </b-button>
                <b-button
                  size="sm"
                  class="float-right"
                  variant="link"
                  @click="createCategoryOfQuestion(qIdx)"
                >
                  {{ labels.category.btn.add }}
                </b-button>
              </b-popover>
            </b-col>
            <b-col
              cols="12"
              xl="6"
              v-if="!['feedback', undefined].includes(question.type)"
            >
              <b-form-group
                :description="
                  editable && question.type !== 'multi-select'
                    ? labels.score.description
                    : ''
                "
              >
                <b-form-select
                  :class="{
                    'readonly-input':
                      !editable || question.type === 'multi-select',
                    clickable: editable && question.type !== 'multi-select',
                  }"
                  class="custom-survey-form-input "
                  :disabled="!editable || question.type === 'multi-select'"
                  :value="question.enableScore"
                  size="lg"
                  required
                  @input="
                    changeListProperty($event, 'questions', qIdx, 'enableScore')
                  "
                >
                  <option :value="true">
                    {{ labels.score.yes }}
                  </option>
                  <option :value="false">
                    {{ labels.score.no }}
                  </option>
                </b-form-select>
                <label class="custom-input-label">
                  {{ labels.score.title }}
                </label>
              </b-form-group>
            </b-col>
          </b-row>
          <div v-if="['multi-select', 'select'].includes(question.type)">
            <hr />
            <small>{{ labels.answers.title }}:</small>
            <div class="answers-wrapper">
              <div
                class="no-content"
                v-if="question.answers && question.answers.length === 0"
              >
                {{ labels.answers.notFound }}
              </div>
              <div
                v-for="(language, index) in availableLanguages"
                :key="`question_${qIdx}_${index}`"
              >
                <b-form-group v-if="isLanguageSelected(index)" class="answers">
                  <div
                    class="answers-row"
                    :class="{ 'no-hover': !editable }"
                    v-for="(answer, idx) in question.answers"
                    :key="`answers_${idx}`"
                  >
                    <i
                      :title="labels.answers.moveUp"
                      class="mdi mdi-chevron-up"
                      :class="{
                        'move-question-answer': idx > 0,
                        'text-gray07': idx === 0,
                      }"
                      v-if="editable"
                      @click="moveUp(qIdx, idx)"
                    >
                    </i>
                    <i
                      :title="labels.answers.moveDown"
                      class="mdi mdi-chevron-down mr-2"
                      :class="{
                        'move-question-answer':
                          idx + 1 < question.answers.length,
                        'text-gray07': idx + 1 === question.answers.length,
                      }"
                      v-if="editable"
                      @click="moveDown(qIdx, idx)"
                    >
                    </i>
                    <img
                      :src="getLanguageIcon(language)"
                      class="language-answers-icon mr-2"
                    />
                    <b-form-input
                      v-if="editable"
                      class="name-form-group custom-survey-form-input  mr-2"
                      :value="answer.text[language]"
                      @input="
                        changeLanguageListOfListProperty(
                          $event,
                          'questions',
                          qIdx,
                          'answers',
                          idx,
                          'text',
                          language
                        )
                      "
                    />
                    <b-form-input
                      v-else
                      class="readonly-input"
                      :value="answer.text[language]"
                      readonly
                    />
                    <div
                      class="form-group classification-wrapper"
                      v-if="question.enableScore"
                    >
                      <b-form-select
                        :class="{
                          'readonly-input': !editable,
                          [answer.classification]: answer.classification,
                          empty: !answer.classification,
                          clickable: editable,
                        }"
                        class="
                          custom-survey-form-input classification form-control
                        "
                        :disabled="!editable"
                        size="lg"
                        required
                        :value="answer.classification"
                        @input="
                          changeListOfListProperty(
                            $event,
                            'questions',
                            qIdx,
                            'answers',
                            idx,
                            'classification'
                          )
                        "
                      >
                        <option :value="null">
                          {{ labels.answers.type.title }}
                        </option>
                        <option value="veryGood">
                          {{ labels.answers.type.veryGood }}
                        </option>
                        <option value="good">
                          {{ labels.answers.type.good }}
                        </option>
                        <option value="neutral">
                          {{ labels.answers.type.neutral }}
                        </option>
                        <option value="bad">
                          {{ labels.answers.type.bad }}
                        </option>
                        <option value="veryBad">
                          {{ labels.answers.type.veryBad }}
                        </option>
                      </b-form-select>
                    </div>
                    <i
                      v-if="editable"
                      :title="labels.delete"
                      class="material-icons clickable"
                      @click="
                        $emit('removeAnswer', {
                          answerId: answer.answer_id
                            ? answer.answer_id
                            : answer.id,
                          questionIndex: qIdx,
                        })
                      "
                    >
                      delete_forever
                    </i>
                  </div>
                </b-form-group>
              </div>
            </div>
            <b-button
              v-if="editable"
              class="text-secondary float-left"
              variant="link"
              @click="$emit('addAnswer', qIdx)"
            >
              <div class="question-header">
                <i class="material-icons">add</i>
                <small>
                  {{ labels.answers.add }}
                </small>
              </div>
            </b-button>
          </div>
        </b-card>
      </div>
    </div>
    <div v-if="editable">
      <b-button
        variant="link"
        class="float-left text-secondary mt-4"
        @click="$emit('addQuestion')"
      >
        <i class="material-icons">add</i>
        {{ labels.add }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

const props = {
  questions: {
    type: Array,
  },
  availableLanguages: {
    type: Array,
  },
  selectedLanguage: {
    type: Number,
  },
  editable: {
    type: Boolean,
    default: true,
  },
  categories: {
    type: Array,
  },
}

export default {
  props,
  components: {
    vSelect: () => import('vue-select'),
  },
  data() {
    return {
      categoryLabel: '',
      indexPick: undefined,
      indexDrop: undefined,
      indexOver: undefined,
      questionTypes: [
        'stars',
        'nps',
        'slide-number',
        'select',
        'multi-select',
        'feedback',
      ],
    }
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
    }),
    labels() {
      return this.resources.component.customSurveys.create.questions
    },
  },
  methods: {
    createCategoryOfQuestion(questionIndex) {
      this.$emit('createCategoryOfQuestion', {
        questionIndex,
        categoryLabel: this.categoryLabel,
      })
      this.categoryLabel = ''
    },
    getImageUrl(a) {
      return `https://static.teamculture.com.br/images/custom-surveys/${a}.svg`
    },
    isLanguageSelected(language) {
      return this.selectedLanguage === language
    },
    getLanguageIcon(language) {
      return `/icons/languages/${language}.svg`
    },
    changeLanguageListProperty(value, collection, index, property, language) {
      this.$emit('changeLanguageListProperty', {
        value,
        collection,
        index,
        property,
        language,
      })
    },
    changeListProperty(value, collection, index, property) {
      this.$emit('changeListProperty', {
        value,
        collection,
        index,
        property,
      })
    },
    showBorder(i) {
      if (this.indexOver !== i || i === this.indexPick) return ''
      return i < this.indexPick ? 'show-top-border' : 'show-bot-border'
    },
    moveQuestion(from, to) {
      if (to < 0 || to >= this.questions.length) return
      this.$emit('moveQuestion', { from, to })
    },
    togglePopoverAddCategory(index, value) {
      this.$emit('togglePopoverAddCategory', { index, value })
    },
    changeLanguageListOfListProperty(
      value,
      collection,
      index,
      listCollection,
      listIndex,
      property,
      language
    ) {
      this.$emit('changeLanguageListOfListProperty', {
        value,
        collection,
        index,
        listCollection,
        listIndex,
        property,
        language,
      })
    },
    changeListOfListProperty(
      value,
      collection,
      index,
      listCollection,
      listIndex,
      property
    ) {
      this.$emit('changeListOfListProperty', {
        value,
        collection,
        index,
        listCollection,
        listIndex,
        property,
      })
    },
    moveUp(qIdx, index) {
      this.$emit('moveUpQuestionAnswer', { qIdx, index })
    },
    moveDown(qIdx, index) {
      this.$emit('moveDownQuestionAnswer', { qIdx, index })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';

[draggable] {
  -moz-user-select: none;
  -khtml-user-select: none;
  -webkit-user-select: none;
  user-select: none;
  -khtml-user-drag: element;
  -webkit-user-drag: element;
}

input:valid:not(.empty) {
  &.popover-name-category {
    border-radius: 10px !important;
    background-color: map-get($theme-colors, 'white') !important;
    border-color: var(--gray07) !important;
  }
}

.popover-language-icon {
  width: 15px;
  height: 15px;
}

.questions-list {
  background-color: map-get($theme-colors, 'white02');

  .no-content {
    text-align: center;
    padding-top: 50px;
    padding-bottom: 40px;
  }

  .show-top-border {
    border-top: 1px solid map-get($theme-colors, 'primary');
  }

  .show-bot-border {
    border-bottom: 1px solid map-get($theme-colors, 'primary');
  }

  .draggable-question {
    border: 0 !important;

    .question-header {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .material-icons {
      line-height: 20px;
      font-size: 20px;
      padding: 3px 0px;
    }

    .card-header {
      font-size: 14px;
      font-weight: bold;
      background-color: map-get($theme-colors, 'white02');

      &.question-card-header {
        &:hover {
          cursor: pointer;
          background-color: map-get($theme-colors, 'light') !important;

          .material-icons {
            opacity: 1;
          }
        }
      }
    }

    .card-body {
      border: 1px solid var(--gray07) !important;

      .tag-wrap img {
        margin-top: 5px;
      }

      .language-icon {
        width: 15px;
        height: 15px;
      }

      .readonly-input {
        background-color: map-get($theme-colors, 'white') !important;
        border-color: map-get($theme-colors, 'white') !important;
        color: #6c757d !important;
      }

      p.readonly-input {
        margin-top: 10px;
        margin-left: 20px;
        margin-right: 10px;
        font-size: 1rem;
      }
    }

    .answers {
      .answers-row {
        display: flex;
        align-items: center;
        width: 100%;
        padding-top: 5px;
        padding-bottom: 5px;
        border-radius: 10px;

        .move-question-answer {
          cursor: pointer;
        }

        &:hover {
          background-color: map-get($theme-colors, 'light') !important;
        }

        &.no-hover:hover {
          background-color: inherit !important;
        }

        .classification-wrapper {
          margin-top: 0px !important;
          font-size: 0.9rem;

          .classification {
            text-align: center;
            width: 175px;
            color: map-get($theme-colors, 'white') !important;

            &.veryGood {
              background-color: #28a745 !important;
            }
            &.good {
              background-color: map-get($theme-colors, 'green') !important;
            }
            &.neutral {
              background-color: #ffc107 !important;
              color: map-get($theme-colors, 'black') !important;
            }
            &.bad {
              background-color: map-get($theme-colors, 'red09') !important;
            }
            &.veryBad {
              background-color: map-get($theme-colors, 'red10') !important;
            }
            &.empty {
              color: map-get($theme-colors, 'black') !important;
            }
          }
        }

        .language-answers-icon {
          width: 20px;
          height: 20px;
        }

        .material-icons {
          font-size: 25px;
        }
      }
    }
  }
}
</style>
